import { injectable } from "inversify";
import { User } from "../../../core/models/users";
import { RegisterUserResult } from "../../../core/models/authorize";
import { SocialError } from "../../../core/models/common";
import {
  getCookieValue,
  getAuthToken,
  getUserCookieRevision,
  isCompactUserCookie,
  USER_COOKIE_VERSION,
  removeCookieValue,
  setCookieValue
} from "../../../utils/cookieStorage";
import databaseInstance from "../../index";
import { IAuthorizeService } from "../../../core/services/authorize";
import { headerify } from "../../APIUtils";
import { jwtDecode } from "jwt-decode";
import { CommonConstants } from "../../../constants/common";
import miscUtils from "../../../utils/miscUtils";

@injectable()
export class AuthorizeService implements IAuthorizeService {
  // @ts-ignore
  public dbInstance: any = databaseInstance;
  private cb: any;
  private static instance: AuthorizeService;
  private hydratedToken: string | null = null;
  private hydratedUser: any = null;
  private hydratedUserRevision = -1;
  private hydrationKey: string | null = null;

  constructor() {
    AuthorizeService.instance = this;
  }

  public static getInstance() {
    if (!AuthorizeService.instance) {
      AuthorizeService.instance = new AuthorizeService();
    }
    return AuthorizeService.instance;
  }

  // Sync the session-expiry watchdog with a freshly issued token so it
  // doesn't force-logout the user (see MainComponent checkCookieExpiry).
  private persistSessionExpiry() {
    const expires = new Date(new Date().getTime() + 14 * 24 * 60 * 60 * 1000);
    localStorage.setItem(`user_token_expires`, expires.toString());
    localStorage.removeItem(`user_logged_out`);
  }

  private persistAuthenticatedUser(user: any, token: string) {
    setCookieValue(CommonConstants.USER_KEY, user);
    setCookieValue(CommonConstants.USER_TOKEN, token);
    this.rememberUser(token, user);
  }

  public getAuthorizableUser: (response: any) => Promise<any> = (
    response,
  ) => {
    return new Promise<any>((resolve, reject) => {
      const user: any = { ...response.user, ...response.data };
      if (user) {
        delete user.user.about_me;
        delete user.user.automated_email_notification;
        this.persistAuthenticatedUser(user.user, user.token);
        // setCookieValue("sidebar", true);
        removeCookieValue("inviterGroup");
        this.cb(user.user);
        resolve(user.user);
      } else {
        throw new SocialError(
          "AuthorizeService/login",
          "User object is invalid!"
        );
      }
    })
  }

  public login: (email: string, password: string, ip: any, ua: any, session_token?: string, group_id?: number, meta?: any) => Promise<any> = (
    email,
    password,
    ip,
    ua,
    session_token,
    group_id,
    meta,
  ) => {
    const payload = meta ? {
      email,
      password,
      ip,
      ua,
      session_token,
      group_id,
      ...meta,
    } : {
      email,
      password,
      ip,
      ua,
      session_token,
      group_id,
    }
    this.dbInstance.interceptors.request.use(headerify);
    return this.dbInstance
      .post("/login", payload)
      .then((result: any) => {
        const user: any = { ...result.user, ...result.data };
        if (user) {
          delete user.user.about_me;
          delete user.user.automated_email_notification;

          this.persistAuthenticatedUser(user.user, user.token);

          this.persistSessionExpiry();

          removeCookieValue("inviterGroup");

          this.cb(user.user);
          return {
            user: user.user,
            membership: user.membership,
          };
        }
        throw new SocialError(
          "AuthorizeService/login",
          "User object is invalid!"
        );
      })
  };

  // Emails a 6-digit sign-in code for checkout; rejects with 404 when no account uses the email.
  public sendWebLoginCode(email: string, group_id?: number) {
    return this.dbInstance.post("/web-login-code", { email, group_id });
  }

  // Signs in with the emailed checkout code and stores the session like login does.
  public webLogin(email: string, code: string) {
    return this.dbInstance
      .post("/web-login", { email, code })
      .then((result: any) => {
        const user: any = { ...(result && result.user), ...(result && result.data) };
        if (!user || !user.user) {
          throw new SocialError(
            "AuthorizeService/webLogin",
            "User object is invalid!"
          );
        }
        delete user.user.about_me;
        delete user.user.automated_email_notification;

        this.persistAuthenticatedUser(user.user, user.token);

        this.persistSessionExpiry();

        removeCookieValue("inviterGroup");

        this.cb(user.user);
        return { user: user.user };
      });
  }

  public async registerUser(user: User) {
    try {
      const payload = { ...user };
      const headers: Record<string, string> = {};

      try {
        // The challenge is bound server-side to this fingerprint; the backend
        // rejects it if the signup presents a different fngpnt.
        const nonceResult: any = await databaseInstance.post("/signup-nonce", {
          fngpnt: payload.fngpnt,
        });
        if (nonceResult && typeof nonceResult.data === "string" && nonceResult.data) {
          headers["X-Signup-Challenge"] = nonceResult.data;
        }
      } catch (nonceError) {
        // Endpoint unavailable — fall back to the legacy path below.
      }

      if (!headers["X-Signup-Challenge"]) {
        let headerChallenge = await miscUtils.getHeaderChallenge(payload.fngpnt as string);
        if (!headerChallenge) {
          try {
            const challengeResult: any = await databaseInstance.get("/signup-challenge");
            headerChallenge = await miscUtils.getHeaderChallenge(
              payload.fngpnt as string,
              challengeResult && challengeResult.data
            );
          } catch (challengeError) {
            // Leave the header empty; the backend will reject and the user can retry.
          }
        }
        headers["X-Access-Ref"] = headerChallenge;
      }

      const result = await databaseInstance.post("/users", payload, { headers });
      const createdUser: any = { ...result.data.user, ...result.data };
      if (createdUser) {
        this.persistAuthenticatedUser(createdUser.user, createdUser.token);
        this.persistSessionExpiry();
        this.cb(createdUser.user);
        return new RegisterUserResult(createdUser.user.id);
      }
      throw new SocialError(
        "AuthorizeService/login",
        "User object is invalid!"
      );
    } catch (error: any) {
      throw new SocialError(error.status, error.data.errors);
    }
  }

  public async getRegisteringUser(result: any) {
    try {
      const createdUser: any = { ...result.data.user, ...result.data };
      if (createdUser) {
        this.persistAuthenticatedUser(createdUser.user, createdUser.token);
        this.persistSessionExpiry();
        this.cb(createdUser.user);
        return new RegisterUserResult(createdUser.user.id);
      }
      throw new SocialError(
        "AuthorizeService/login",
        "User object is invalid!"
      );
    } catch (error: any) {
      throw new SocialError(error.status, error.data.errors);
    }
  }

  public async forgotPassword(email: string, session_token?: any, groupId?: any) {
    const payload = {
      email,
      session_token
    };

    if (groupId) {
      //@ts-ignore
      payload.group_id = groupId;
    }
    const result = await databaseInstance.post("/password/email", payload);
    return result;
  }

  public resetPassword: (
    email: string,
    verification: string,
    password: string,
    passwordConfirmation: string
  ) => Promise<any> = (email, verification, password, passwordConfirmation) => {
    return this.dbInstance.post("/password/reset", {
      email,
      password,
      token: verification,
      password_confirmation: passwordConfirmation
    });
  };

  public verifyPasswordToken: (
    email: string,
    token: string,
  ) => Promise<any> = (email, token) => {
    return this.dbInstance.post("/password/validate-token", {
      email,
      token,
    });
  };

  public verifyAccount: (userId: number, code: number) => Promise<any> = (
    userId,
    code
  ) => {
    return this.dbInstance.post(`/email/verify/${userId}/${code}`, {});
  };

  public resendVerificationCode: (groupId?: number) => Promise<any> = (groupId) => {
    if (groupId) {
      return this.dbInstance.post(`/email/resend`, { group_id: groupId });
    }
    return this.dbInstance.post(`/email/resend`, {});
  };

  public onAuthStateChanged: (callBack: any) => void = callBack => {
    this.cb = callBack;
    const token = getAuthToken();
    let userData: any = getCookieValue(CommonConstants.USER_KEY);
    if (!token) {
      removeCookieValue(CommonConstants.USER_KEY);
      this.clearHydration();
      callBack(null);
      return;
    }

    let tokenData: any = null;
    try {
      tokenData = jwtDecode(token);
    } catch (_error) {
      callBack(null);
      return;
    }
    if (tokenData.exp < Date.now() / 1000) {
      callBack(null);
      return;
    }

    if (!userData || userData.id === undefined || userData.id === null) {
      const id = tokenData.sub || tokenData.id || tokenData.user_id;
      if (id === undefined || id === null) {
        callBack(null);
        return;
      }
      userData = { __v: USER_COOKIE_VERSION, id };
      setCookieValue(CommonConstants.USER_KEY, userData);
    }

    if (!isCompactUserCookie(userData)) {
      setCookieValue(CommonConstants.USER_KEY, userData);
      this.rememberUser(token, userData);
      callBack(userData);
      return;
    }

    const isHydrated = this.hydratedToken === token && this.hydratedUser &&
      this.hydratedUserRevision === getUserCookieRevision() &&
      String(this.hydratedUser.id) === String(userData.id);
    callBack(isHydrated ? this.hydratedUser : userData);
    if (!isHydrated) {
      this.hydrateUser(token, userData.id);
    }
  };

  // Loads the full profile behind the compact user cookie; the session stays on the compact user if it fails.
  private hydrateUser(token: string, id: any) {
    const key = `${token}:${id}`;
    if (this.hydrationKey === key) return;
    this.hydrationKey = key;

    this.dbInstance.get(`/users/${encodeURIComponent(id)}`).then((response: any) => {
      if (this.hydrationKey !== key) return;
      this.hydrationKey = null;
      const profile = response && response.data;
      if (!profile || String(profile.id) !== String(id) || getAuthToken() !== token) return;
      setCookieValue(CommonConstants.USER_KEY, profile);
      this.rememberUser(token, profile);
      this.cb(profile);
    }).catch((error: any) => {
      if (this.hydrationKey !== key) return;
      this.hydrationKey = null;
      if (error && error.status === 401 && getAuthToken() === token) {
        removeCookieValue(CommonConstants.USER_KEY);
        removeCookieValue(CommonConstants.USER_TOKEN);
        this.cb(null);
      }
    });
  }

  private rememberUser(token: string, user: any) {
    this.hydratedToken = token;
    this.hydratedUser = user;
    this.hydratedUserRevision = getUserCookieRevision();
  }

  private clearHydration() {
    this.hydratedToken = null;
    this.hydratedUser = null;
    this.hydratedUserRevision = -1;
    this.hydrationKey = null;
  }

  public logout() {
    this.clearHydration();
    removeCookieValue(CommonConstants.USER_KEY);
    removeCookieValue(CommonConstants.USER_TOKEN);
    localStorage.removeItem("user_token_expires");
    localStorage.removeItem("next_link");
    localStorage.removeItem("lecture-sidebar");
    this.cb(null);
  }

  public confirmInviteEmail: (
    groupId: string,
    email: string,
    inviteConfigId: number,
  ) => Promise<any> = (groupId, email, inviteConfigId) => {
    return this.dbInstance.post(`/groups/${groupId}/invitation-configs/${inviteConfigId}/email-check`, {
      email
    });
  };

  public confirmYouAreHumanOnCommunity: (
    groupId: number,
    tokenString: string,
  ) => Promise<void> = (groupId, tokenString) => {
    return new Promise<void>((resolve, reject) => {
      this.dbInstance
        .post(`/groups/${groupId}/verify-turnstile`, { token: tokenString })
        .then((response: any) => resolve(response))
        .catch((error: any) => {
          reject(error)
          new Error("Something went wrong!")
        })
    });
  };

  public confirmYouAreHuman: (tokenString: string) => Promise<void> = (tokenString) => {
    return new Promise<void>((resolve, reject) => {
      this.dbInstance
        .post(`/verify-turnstile`, { token: tokenString })
        .then((response: any) => resolve(response))
        .catch((error: any) => {
          reject(error)
          new Error("Something went wrong!")
        })
    });
  };

  public getLoginToken: () => Promise<void> = () => {
    return new Promise<void>((resolve, reject) => {
      this.dbInstance
        .post(`/token-switch`)
        .then((response: any) => resolve(response))
        .catch((error: any) => {
          reject(error)
          new Error("Something went wrong!")
        })
    });
  };

}
